/**
 * 線上 Office 編輯頁（Task-33 3.2b）· 獨立新分頁（不套 MainLayout）。
 *
 * my_drive「線上編輯」→ window.open('/office/{fileObjectId}', '_blank', 'noopener,noreferrer')。
 * 本頁向 backend 換 WOPI token → iframe embed Collabora（WOPI 標準必 iframe；主頁面走新分頁，
 * 見 memory office-open-new-tab-not-iframe）。Collabora 依 --o:net.frame_ancestors 允許 embed。
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconButton, Typography } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import myDriveService from './services/myDriveService';
import AgenticChatPanel from './components/AgenticChatPanel';
import { buildOfficeActionMessages } from './services/officeActionMessages';
import { checkOfficeChatAvailable } from '../../../services/eeAiService';

// 3.2h · Collabora 關檔事件名 upstream 版本有別（UI_Close 為主，Action_Close/App_Close 為
// 保險 fallback）→ 三個都收。
const COLLABORA_CLOSE_EVENTS = new Set(['UI_Close', 'Action_Close', 'App_Close']);
// post-a · AI 面板可縮放（default 隱藏 · 每次重置寬度）
const DEFAULT_PANEL_WIDTH = 400;
const MIN_PANEL_WIDTH = 300;
const MAX_PANEL_WIDTH_RATIO = 0.6;

export default function OfficeEditorPage() {
  const { fileObjectId } = useParams<{ fileObjectId: string }>();
  const [searchParams] = useSearchParams();
  // 3.2c BUG FIX 2：?mode=view → 明確要求 'read' token（Collabora readonly）；
  // 省略 → backend auto-decide（owner/edit-grant→edit）。
  const viewMode = searchParams.get('mode') === 'view';
  // Sprint 4 · AI 面板需檔名/mime（由 MyDrivePage 開啟時帶入 query；缺省 fallback 通用工具）
  const mimeType = searchParams.get('mime') || '';
  const filename = searchParams.get('name') || '';
  const { t, i18n } = useTranslation();
  const [collaboraUrl, setCollaboraUrl] = useState('');
  const [wopiToken, setWopiToken] = useState('');
  const [error, setError] = useState('');
  const [closeHint, setCloseHint] = useState(false);   // 3.2h · window.close 被瀏覽器擋時顯手動提示
  const [aiAvailable, setAiAvailable] = useState<boolean | null>(null);   // Layer 3 UI gate（null=loading）
  const [panelOpen, setPanelOpen] = useState(false);            // post-a · default 隱藏
  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH);
  const [dragging, setDragging] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // post-a · Layer 3 probe：訂閱 approved + provider active 才顯 AI 助手 button（未開通根本看不到）
  useEffect(() => {
    checkOfficeChatAvailable()
      .then((r) => setAiAvailable(!!r.available))
      // silent ok: probe fail（network/500）→ 當作沒開通 hide button，不 alert 干擾 UX
      .catch(() => setAiAvailable(false));
  }, []);

  // post-a · 拖 divider 調面板寬度（native mouse handler · 無第三方 dep）
  useEffect(() => {
    if (!dragging) return;
    const maxWidth = window.innerWidth * MAX_PANEL_WIDTH_RATIO;
    const onMove = (e: MouseEvent) => {
      const w = window.innerWidth - e.clientX;
      setPanelWidth(Math.max(MIN_PANEL_WIDTH, Math.min(maxWidth, w)));
    };
    const onUp = () => setDragging(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [dragging]);

  // 每次開面板重置寬度（不記憶 · 避免誤設過窄）
  const openPanel = () => { setPanelWidth(DEFAULT_PANEL_WIDTH); setPanelOpen(true); };

  // 新分頁：斷開 opener（防 reverse tabnabbing；window.open 已帶 noopener，此為雙保險）
  useEffect(() => {
    if (window.opener) window.opener = null;
  }, []);

  useEffect(() => {
    if (!fileObjectId) return;
    myDriveService.issueOfficeToken(Number(fileObjectId), viewMode ? 'read' : undefined)
      .then((resp) => { setCollaboraUrl(resp.collabora_url || ''); setWopiToken(resp.token); })
      .catch((err) => { setError(err?.response?.data?.detail?.code || 'ERR330036'); });
  }, [fileObjectId, viewMode]);

  // 3.2h · 監聽 Collabora postMessage：X 關檔 → 關閉分頁。
  // security：只信 Collabora origin 的訊息。事件到達需後端 WOPI CheckFileInfo 回 PostMessageOrigin
  // 才會由 Collabora 送出（見 PR 說明；目前後端未設 → listener 為 forward-ready，事件到才 close）。
  useEffect(() => {
    if (!collaboraUrl) return;
    let collaboraOrigin = '';
    try { collaboraOrigin = new URL(collaboraUrl).origin; } catch { return; }
    const handler = (event: MessageEvent) => {
      if (event.origin !== collaboraOrigin) return;   // 跨來源防護：非 Collabora 一律忽略
      let data: { MessageId?: string } | null = null;
      try {
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch { return; }
      if (!data || typeof data !== 'object' || !data.MessageId) return;
      if (COLLABORA_CLOSE_EVENTS.has(data.MessageId)) {
        window.close();
        // script-opened 分頁 window.close 應可行；若被政策擋（非 script-opened / 有 history）→ 顯提示
        setTimeout(() => { if (!window.closed) setCloseHint(true); }, 300);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [collaboraUrl]);

  // Collabora handshake：iframe 載入後送 Host_PostmessageReady，Collabora 才會回送 UI 事件。
  const handleIframeLoad = () => {
    try {
      const origin = new URL(collaboraUrl).origin;
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({ MessageId: 'Host_PostmessageReady' }), origin);
    } catch { /* silent ok: origin 解析失敗 → 略過 handshake（不影響檢視/編輯本身） */ }
  };

  // 4-post-b · 依 mime 分派插入：xlsx 走 Action_Paste（tab 分隔展多 cells·InsertText 對 Calc 無效）；
  // docx/pptx 走 .uno:InsertText（游標處插入·有選取即取代 → 同時服務 insert/refine）。
  const sendToOffice = (content: string, _style?: string) => {
    if (!content) return;
    try {
      const origin = new URL(collaboraUrl).origin;
      const win = iframeRef.current?.contentWindow;
      if (!win) return;
      if (mimeType.includes('spreadsheet')) {
        win.postMessage(JSON.stringify({
          MessageId: 'Action_Paste',
          Values: { Mimetype: 'text/plain;charset=utf-8', Data: content },
        }), origin);
      } else {
        win.postMessage(JSON.stringify({
          MessageId: 'Send_UNO_Command',
          Values: { Command: '.uno:InsertText', Args: { Text: { type: 'string', value: content } } },
        }), origin);
      }
    } catch { /* silent ok: origin parse fail → 略過插入 */ }
  };

  // hotfix-2 · xlsx 進階工具：結構化 postMessage 序列（公式貼上 / 合併 / 凍結 / 格式 / 新 sheet）。
  // Cool 需時間處理連續指令 → 每則間隔 120ms 依序送（沿用 4-post-b 的 stagger 經驗）。
  const sendActionToOffice = (name: string, input: Record<string, unknown>): void => {
    const msgs = buildOfficeActionMessages(name, input);
    if (!msgs) return;   // 不支援的 tool（backend 未 register·理論上不會到這）
    try {
      const origin = new URL(collaboraUrl).origin;
      const win = iframeRef.current?.contentWindow;
      if (!win) return;
      msgs.forEach((m, i) => {
        setTimeout(() => win.postMessage(JSON.stringify(m), origin), i * 120);
      });
    } catch { /* silent ok: origin parse fail → 略過 */ }
  };

  // 4-post-b · 取消 auto-apply → Collabora .uno:Undo
  const sendUndo = () => {
    try {
      const origin = new URL(collaboraUrl).origin;
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({ MessageId: 'Send_UNO_Command', Values: { Command: '.uno:Undo' } }), origin);
    } catch { /* silent ok: origin parse fail */ }
  };

  if (error) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 12, color: '#555' }}>
        <div style={{ fontSize: 48 }}>⚠️</div>
        <div>{t('myDrive.officeAuthExpired', '授權已失效，請回到我的網路硬碟重新開啟')}</div>
      </div>
    );
  }
  if (!collaboraUrl || !wopiToken) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {t('common.loading', '載入中…')}
      </div>
    );
  }

  // v0：hardcode dist/cool.html（PoC baseline）。TODO：改讀 collabora /hosting/discovery 的 urlsrc
  // （build hash runtime，見 memory collabora-lab-poc-standard-baseline），避免 pinned build hash 對不上。
  const wopiSrc = `${window.location.origin}/api/wopi/files/${fileObjectId}`;
  // 讀取模式加 permission=readonly（Collabora UI 直接鎖唯讀；WOPI CheckFileInfo
  // UserCanWrite=false 仍為權威控制，此為 UI 層雙保險）。
  const coolUrl = `${collaboraUrl}/browser/dist/cool.html`
    + `?WOPISrc=${encodeURIComponent(wopiSrc)}`
    + `&access_token=${encodeURIComponent(wopiToken)}`
    + `&lang=${encodeURIComponent(i18n.language)}`
    + (viewMode ? '&permission=readonly' : '');

  // Layer 3 UI gate：已開通（probe available）+ 編輯模式才顯 AI 助手 button（唯讀檢視插入無意義）
  const showAiButton = aiAvailable === true && !viewMode;
  const panelVisible = showAiButton && panelOpen;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {closeHint && (
        <div style={{ padding: '8px 12px', background: '#fff3cd', color: '#664d03',
                      borderBottom: '1px solid #ffe69c', fontSize: 14, textAlign: 'center' }}>
          {t('myDrive.officeCloseTabHint', '文件已關閉，請手動關閉此分頁')}
        </div>
      )}
      {/* Toolbar：檔名 + AI 助手 toggle（僅已開通租戶顯示 button）*/}
      <div style={{ display: 'flex', alignItems: 'center', padding: '4px 12px',
                    borderBottom: '1px solid #ddd', gap: 8 }}>
        <Typography variant="body2" noWrap sx={{ flex: 1, color: '#555' }}>{filename || 'Office'}</Typography>
        {showAiButton && (
          <IconButton size="small"
            color={panelOpen ? 'primary' : 'default'}
            onClick={() => (panelOpen ? setPanelOpen(false) : openPanel())}
            title={panelOpen ? t('aiOffice.closeHint', '關閉 AI 助手') : t('aiOffice.openHint', '開啟 AI 助手')}
            aria-label={t('aiOffice.toggle', 'AI 助手')}>
            <SmartToyIcon />
          </IconButton>
        )}
      </div>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <iframe
            ref={iframeRef}
            title="Office"
            src={coolUrl}
            onLoad={handleIframeLoad}
            style={{ width: '100%', height: '100%', border: 0,
                     pointerEvents: dragging ? 'none' : 'auto' }}
            allow="clipboard-read; clipboard-write"
          />
        </div>
        {panelVisible && (
          <>
            {/* Divider · 拖曳調寬 */}
            <div onMouseDown={(e) => { e.preventDefault(); setDragging(true); }}
              role="separator" aria-label={t('aiOffice.resize', '拖曳調整 AI 助手寬度')}
              style={{ width: 5, cursor: 'ew-resize', flexShrink: 0,
                       background: dragging ? '#1976d2' : '#e0e0e0',
                       transition: dragging ? 'none' : 'background 0.2s' }} />
            <div style={{ width: panelWidth, flexShrink: 0, minWidth: 0, borderLeft: '1px solid #ccc' }}>
              <AgenticChatPanel
                fileObjectId={fileObjectId ? Number(fileObjectId) : undefined}
                mimeType={mimeType}
                filename={filename}
                onInsert={(content, style) => sendToOffice(content, style)}
                onRefineReplace={(content) => sendToOffice(content)}
                onOfficeAction={sendActionToOffice}
                onClose={() => setPanelOpen(false)}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
